import { useState, useLayoutEffect } from "react";
import { Combobox } from "cerulean";
import { Row } from "@narmi/design_system";

interface RecipientSelectorProps {
  field?: string;
  value: string;
  recipients: API.Recipient[];
  label: string;
  onChange: (recipientId: API.Recipient["id"]) => void;
  onAddRecipientClick: () => void;
  error?: string;
  displayBottomAction?: boolean;
}

// this recipient selector is used in the ACH and Wire flows (ACH Recipient Screen and International Wire Form)
const RecipientSelector = ({
  value,
  recipients,
  label,
  onChange,
  onAddRecipientClick,
  displayBottomAction,
  error,
}: RecipientSelectorProps) => {
  const selectedRecipient = recipients.find((r) => r.id === value);
  const [inputValue, setInputValue] = useState<string>(
    selectedRecipient ? selectedRecipient.name : ""
  );

  // whenever `value` changes externally, update inputValue (what is displayed in the input) to match
  useLayoutEffect(() => {
    setInputValue(selectedRecipient?.name ?? "");
  }, [value, selectedRecipient]);

  const handleChange = (c: string) => {
    onChange(c as API.RecipientId);
    const pickedRecipient = recipients.find((r) => r.id === c);
    setInputValue(pickedRecipient?.name ?? "");
  };

  const handleAddRecipientClick = () => {
    onChange("" as API.RecipientId);
    setInputValue("");
    onAddRecipientClick();
  };

  return (
    <Combobox
      label={label}
      value={value}
      onChange={handleChange}
      errorText={error}
      inputValue={inputValue}
      onInputChange={(updatedValue: string) => {
        setInputValue(updatedValue);
      }}
      clearSelectionOnAction={true}
    >
      {recipients.map((r) => (
        <Combobox.Item key={r.id} value={r.id} searchValue={r.name}>
          <Row gapSize="xxs">
            <Row.Item>{r.name}</Row.Item>
            {r.nickname ? (
              <Row.Item shrink>
                <span className="fontSize--xs fontColor--secondary">
                  {r.nickname}
                </span>
              </Row.Item>
            ) : null}
          </Row>
        </Combobox.Item>
      ))}
      {displayBottomAction && (
        <Combobox.Action
          label="Add a new recipient"
          onSelect={handleAddRecipientClick}
        />
      )}
    </Combobox>
  );
};

export default RecipientSelector;
